import { ReactElement } from 'react';
import { useTheme } from '@chakra-ui/react';
import { TFunction } from 'react-i18next';
import { CustomIcon } from 'components';
import { Tag, TagLeftIcon, Text } from '@chakra-ui/react';
import { Cloud, AirplaneTilt, CalendarBlank, SquaresFour, Boat } from 'components/Icons/Icons';
import { ReactComponent as carIcon } from 'assets/svgs/car_icon.svg';
import { ReactComponent as petsIcon } from 'assets/svgs/pets.svg';
import { ReactComponent as giftIcon } from 'assets/svgs/gift.svg';

export function getOffsetTypeIcon(orderType: string, isGift?: boolean): ReactElement {
  const { colors } = useTheme();

  if (isGift) {
    return <CustomIcon name="gift" withBackground preventSVGFill color={colors.other.orderPets} boxSize="30px" minW="30px" p="6px" />;
  }

  switch (orderType) {
    case 'Flights':
      return <CustomIcon name="plane" withBackground color={colors.other.orderFlight} boxSize="30px" minW="30px" p="8px" />;
    case 'Events':
      return <CustomIcon name="event" withBackground color={colors.other.orderEvent} boxSize="30px" minW="30px" p="7px" />;
    case 'Carbon':
      return <CustomIcon name="carbon" withBackground color={colors.other.orderCarbon} boxSize="30px" minW="30px" p="6px" />;
    case 'Lifestyle':
      return <CustomIcon name="lifestyle" withBackground color={colors.other.orderLifestyle} boxSize="30px" minW="30px" p="6px" />;
    case 'Pets':
      return <CustomIcon name="pets" withBackground color={colors.other.orderPets} boxSize="30px" minW="30px" p="6px" />;
    case 'Car':
      return <CustomIcon name="car" withBackground color={colors.other.orderCar} boxSize="30px" minW="30px" p="6px" />;
    case 'Gift':
      return <CustomIcon name="gift" withBackground preventSVGFill color={colors.other.orderPets} boxSize="30px" minW="30px" p="6px" />;
    default:
      return <CustomIcon name="other" withBackground color={colors.other.orderOther} boxSize="30px" minW="30px" p="6px" />;
  }
}

export function getOffsetTypeCorporateIcon(orderType: string, isGift?: boolean, iconDescription?: string): ReactElement {
  let leftIcon;
  if (isGift) {
    leftIcon = giftIcon;
  } else {
    switch (orderType) {
      case 'Flights':
        leftIcon = AirplaneTilt;
        break;

      case 'Events':
        leftIcon = CalendarBlank;
        break;

      case 'Carbon':
        leftIcon = Cloud;
        break;

      case 'Pets':
        leftIcon = petsIcon;
        break;

      case 'Car loan':
      case 'Car':
        leftIcon = carIcon;
        break;

      case 'Gift':
        leftIcon = giftIcon;
        break;

      case 'Sea freight':
      case 'Shipping':
        leftIcon = Boat;
        break;

      default:
        leftIcon = SquaresFour;
        break;
    }
  }

  return (
    <Tag maxW="100%">
      <TagLeftIcon as={leftIcon} margin="0 3px" />
      <Text as="span" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" data-attribute="corporate-transaction-category">
        {iconDescription}
      </Text>
    </Tag>
  );
}

export function getOffsetTypeLabel(orderType: string, t: TFunction<string[]>, isGift?: boolean): string {
  if (isGift) {
    return t('orderType.gift');
  }

  const formattedOrderType = (orderType ?? 'Other').toLowerCase().replace(' ', '');

  switch (formattedOrderType) {
    case 'flights':
      return t('orderType.flights');
    case 'events':
      return t('orderType.events');
    case 'carbon':
      return t('orderType.carbon');
    case 'lifestyle':
      return t('orderType.lifestyle');
    case 'pets':
      return t('orderType.pets');
    case 'car':
    case 'carloan':
      return t('orderType.car');
    case 'employeebenefit':
      return t('orderType.employeebenefit');
    case 'airfreightsubscription':
      return t('orderType.airFreightSubscription');
    case 'shipping':
    case 'seafreight':
      return t('orderType.shipping');
    case 'flightsubscription':
      return t('orderType.flightSubscription');
    case 'other':
      return t('orderType.other');
    default:
      return orderType;
  }
}

export const transactionProductsName = {
  flight: 'Flight',
  bulk: 'Bulk',
  event: 'Event',
  shipping: 'Shipping',
};

export function mapTransactionProducts(orderCategory: string): string {
  switch (orderCategory.toLowerCase()) {
    case 'carbon':
      return transactionProductsName.bulk;
    case 'flights':
    case 'airfreightsubscription':
      return transactionProductsName.flight;
    case 'events':
      return transactionProductsName.event;
    case 'shipping':
    case 'seafreight':
      return transactionProductsName.shipping;
    default:
      return orderCategory;
  }
}
